// MITRE ATT&CK Technique Mappings
// Common techniques detected by ShadowGuard

export const MITRE_TECHNIQUES: Record<string, { name: string; description: string; category: string }> = {
  // Reconnaissance
  'T1595': { name: 'Active Scanning', description: 'Scanning target networks/systems', category: 'Reconnaissance' },
  'T1595.001': { name: 'Scanning IP Blocks', description: 'Scanning IP address ranges', category: 'Reconnaissance' },
  'T1595.002': { name: 'Vulnerability Scanning', description: 'Automated vulnerability scanning', category: 'Reconnaissance' },
  'T1592': { name: 'Gather Victim Host Info', description: 'Collecting system information', category: 'Reconnaissance' },
  'T1598': { name: 'Phishing for Information', description: 'Social engineering attempts', category: 'Reconnaissance' },
  'T1596': { name: 'Search Open Technical Databases', description: 'Querying public databases', category: 'Reconnaissance' },
  'T1590': { name: 'Gather Victim Network Info', description: 'Mapping network topology', category: 'Reconnaissance' },
  'T1589': { name: 'Gather Victim Identity Info', description: 'Collecting identity information', category: 'Reconnaissance' },
  
  // Initial Access
  'T1190': { name: 'Exploit Public Application', description: 'Exploiting web vulnerabilities', category: 'Initial Access' },
  'T1133': { name: 'External Remote Services', description: 'Accessing remote services', category: 'Initial Access' },
  'T1078': { name: 'Valid Accounts', description: 'Using stolen credentials', category: 'Initial Access' },
  'T1566': { name: 'Phishing', description: 'Phishing attempts', category: 'Initial Access' },
  
  // Credential Access
  'T1110': { name: 'Brute Force', description: 'Password guessing attacks', category: 'Credential Access' },
  'T1110.001': { name: 'Password Guessing', description: 'Trying common passwords', category: 'Credential Access' },
  'T1110.003': { name: 'Password Spraying', description: 'Low-and-slow credential stuffing', category: 'Credential Access' },
  'T1552': { name: 'Unsecured Credentials', description: 'Searching for exposed secrets', category: 'Credential Access' },
  'T1552.001': { name: 'Credentials In Files', description: 'Looking for hardcoded secrets', category: 'Credential Access' },
  'T1552.004': { name: 'Private Keys', description: 'Searching for SSH/TLS keys', category: 'Credential Access' },
  
  // Discovery
  'T1083': { name: 'File/Directory Discovery', description: 'Enumerating filesystem', category: 'Discovery' },
  'T1018': { name: 'Remote System Discovery', description: 'Finding network systems', category: 'Discovery' },
  'T1046': { name: 'Network Service Discovery', description: 'Port scanning services', category: 'Discovery' },
  'T1087': { name: 'Account Discovery', description: 'Enumerating user accounts', category: 'Discovery' },
  
  // Collection
  'T1005': { name: 'Data from Local System', description: 'Accessing local data stores', category: 'Collection' },
  'T1039': { name: 'Data from Network Share', description: 'Accessing network shares', category: 'Collection' },
  'T1213': { name: 'Data from Information Repos', description: 'Accessing databases/repos', category: 'Collection' },
  
  // Exfiltration
  'T1041': { name: 'Exfiltration Over C2', description: 'Data theft via command channel', category: 'Exfiltration' },
  'T1048': { name: 'Exfiltration Over Alternative Protocol', description: 'Using non-standard protocols', category: 'Exfiltration' },
  
  // Defense Evasion
  'T1562': { name: 'Impair Defenses', description: 'Disabling security tools', category: 'Defense Evasion' },
  'T1562.001': { name: 'Disable Security Tools', description: 'Turning off AV/EDR', category: 'Defense Evasion' },
  'T1027': { name: 'Obfuscation', description: 'Hiding malicious code', category: 'Defense Evasion' },
  'T1036': { name: 'Masquerading', description: 'Disguising as legitimate', category: 'Defense Evasion' },
  
  // Persistence
  'T1098': { name: 'Account Manipulation', description: 'Modifying accounts for persistence', category: 'Persistence' },
  'T1136': { name: 'Create Account', description: 'Creating backdoor accounts', category: 'Persistence' },
  'T1053': { name: 'Scheduled Task/Job', description: 'Creating persistence tasks', category: 'Persistence' },
  'T1547': { name: 'Boot/Logon Autostart', description: 'Auto-execution on boot', category: 'Persistence' },
};

export function getTechniqueName(techniqueId: string): string {
  const technique = MITRE_TECHNIQUES[techniqueId];
  return technique?.name || techniqueId;
}

export function getTechniqueDescription(techniqueId: string): string {
  const technique = MITRE_TECHNIQUES[techniqueId];
  return technique?.description || 'Unknown technique';
}

export function getTechniqueCategory(techniqueId: string): string {
  const technique = MITRE_TECHNIQUES[techniqueId];
  return technique?.category || 'Unknown';
}

export function getTechniqueCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    'Reconnaissance': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Initial Access': 'bg-red-500/20 text-red-400 border-red-500/30',
    'Credential Access': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Discovery': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Collection': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Exfiltration': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    'Defense Evasion': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    'Persistence': 'bg-green-500/20 text-green-400 border-green-500/30',
  };
  return colors[category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
}
